import { ALL_LNGS_NAMESPACES } from '@ingenico/util-i18n-config';
import { Banner } from '@ingenico/react-ui/banner';
import { CoreContainer } from '@ingenico/react-ui/core-container';
import { EmptyList } from '@ingenico/react-ui/notice-card';
import { LinearProgress } from '@mui/material';
import classNames from 'classnames';
import { useSetAtom } from 'jotai';
import type { ReactElement } from 'react';
import { useCallback, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { useDeviceManagementError } from '../../../hooks/useDeviceManagementError.hook';
import { ScreenShareButton } from '../../logical-device-actions/LogicalDeviceActions.ctrl';
import { LogicalDeviceApplicationsCard } from '../../logical-device-applications-card/LogicalDeviceApplicationsCard.ctrl';
import { LogicalDeviceBatteryInformation } from '../../logical-device-battery-information/LogicalDeviceBatteryInformation.ctrl';
import { LogicalDeviceFirmwareCard } from '../../logical-device-firmware-card/LogicalDeviceFirmwareCard.ctrl';
import { LogicalDeviceLocationCard } from '../../logical-device-location-card/LogicalDeviceLocationCard.ctrl';
import { LogicalDeviceSIMInformation } from '../../logical-device-sim-information/LogicalDeviceSIMInformation.ctrl';
import { LogicalDeviceTerminalDetailsCard } from '../../logical-device-terminal-details-card/LogicalDeviceTerminalDetailsCard.ctrl';
import { MinimalDeviceLayout } from '../../minimal-device-layout/MinimalDeviceLayout';
import { MyTerminalDetailedCard } from '../../my-terminals-detailed-list/MyTerminalsDetailedList.ctrl';
import { TetraCallSettingsCard } from '../../tetra-call-settings-card/TetraCallSettingsCard.ctrl';
import { useTetraDeviceInfoCard } from '../../tetra-device-info-card/useTetraDeviceInfoCard';
import { openUnblockDeviceModalAtom } from '../../unblock-device-modal/UnblockDeviceModal.atom';
import { BlockedDeviceBanner } from './BlockedDeviceBanner.ctrl';
import type { LogicalDeviceInfoProps } from './LogicalDeviceInfo.model';
import styles from './LogicalDeviceInfo.module.scss';
import { useLogicalDeviceInfoAttachedVm } from './useLogicalDeviceInfoAttached.vm';

export function LogicalDeviceInfoAttached({
  logicalDeviceId,
  logicalDeviceDetailsQuery,
  deviceIdV2,
  isSuspended,
  onRightColumnExtensionContainerReady,
}: LogicalDeviceInfoProps): ReactElement {
  const { t } = useTranslation([ALL_LNGS_NAMESPACES.LOGICAL_DEVICE, ALL_LNGS_NAMESPACES.PHYSICAL_DEVICE]);
  const { getErrorMessage } = useDeviceManagementError();
  const setIsModalOpen = useSetAtom(openUnblockDeviceModalAtom);

  const {
    connectionStatus,
    connectivity,
    deviceBatteryInformation,
    deviceMetrics,
    deviceSIMInformation,
    general,
    geofencing,
    handleOpenScreenShare,
    handleRefreshConfigurations,
    hardware,
    isError,
    isLoading,
    isRefetchActionOngoing,
    isScreenShareAvailable,
    isScreenShareDisabled,
    isScreenShareSessionOpened,
    isTetraDevice,
    isToShowDeviceDetails,
    logicalDeviceDetails,
    logicalDeviceError,
    logicalDeviceProps,
    physicalDeviceInfo,
    shouldDisplayMinimalDeviceLayout,
    shouldShowLogicalDevice,
    shouldShowTerminalDetails,
    tetraCallScheduling,
  } = useLogicalDeviceInfoAttachedVm({
    logicalDeviceId,
    logicalDeviceDetailsQuery,
    deviceIdV2,
  });

  const isBlocked = useMemo(
    () => logicalDeviceDetailsQuery.data?.attachedPhysicalDevices?.[0]?.isBlocked === true && !isSuspended,
    [logicalDeviceDetailsQuery.data, isSuspended],
  );

  const handleUnblock = useCallback(() => {
    setIsModalOpen(true);
  }, [setIsModalOpen]);

  const { image: tetraImage } = useTetraDeviceInfoCard({
    physicalDevice: physicalDeviceInfo,
    attachedPhysicalDevice: logicalDeviceDetails?.attachedPhysicalDevices?.[0],
  });

  if (isLoading) {
    return <LinearProgress />;
  }
  if (isError) {
    return (
      <Banner
        message={
          logicalDeviceError
            ? getErrorMessage(logicalDeviceError)
            : t('error.default', { ns: ALL_LNGS_NAMESPACES.COMMON })
        }
      />
    );
  }

  if (shouldDisplayMinimalDeviceLayout) {
    return (
      <>
        {(isBlocked || isSuspended) && (
          <CoreContainer>
            <div className={styles.bannersWrapper}>
              <BlockedDeviceBanner isBlocked={isBlocked} onUnblock={handleUnblock} />
              {isSuspended && (
                <Banner
                  message={t('logicalDevice.suspendedBanner.message', { ns: ALL_LNGS_NAMESPACES.LOGICAL_DEVICE })}
                  severity="Error"
                />
              )}
            </div>
          </CoreContainer>
        )}
        <MinimalDeviceLayout
          logicalDeviceDetails={logicalDeviceDetails}
          deviceMetrics={deviceMetrics}
          shouldShowTerminalDetails={shouldShowTerminalDetails}
          shouldDisplayCustomerLayout={shouldDisplayMinimalDeviceLayout}
          physicalDeviceInfo={physicalDeviceInfo}
          onRightColumnExtensionContainerReady={onRightColumnExtensionContainerReady}
        />
      </>
    );
  }

  return (
    <div>
      <CoreContainer
        customMainClassName={classNames(styles.container, { [styles.container__withBanner]: isSuspended })}
      >
        <div className={styles.bannersWrapper}>
          {isSuspended && (
            <Banner
              message={t('logicalDevice.suspendedBanner.message', { ns: ALL_LNGS_NAMESPACES.LOGICAL_DEVICE })}
              severity="Error"
            />
          )}
          <BlockedDeviceBanner isBlocked={isBlocked} onUnblock={handleUnblock} />
        </div>
        <div className={classNames(styles.contentWrapper)}>
          <div className={classNames(styles.leftSideElementsContainer)}>
            {isToShowDeviceDetails ? (
              <>
                {isScreenShareAvailable && (
                  <div className={styles.deviceInfo__actions__mobile}>
                    <ScreenShareButton
                      isScreenShareAvailable={isScreenShareAvailable}
                      isScreenShareDisabled={isScreenShareDisabled}
                      isScreenShareSessionInProgress={isScreenShareSessionOpened}
                      onClickScreenShare={handleOpenScreenShare}
                    />
                  </div>
                )}
                <MyTerminalDetailedCard
                  connectivity={connectivity}
                  customClassName={{
                    card: styles.myTerminalDetailedCard,
                    hardwareContainer: styles.hardwareContainer,
                    divider: styles.divider,
                    hardwareChartsItemContainer: styles.hardwareChartsItemContainer,
                    hardwareChartsContainer: styles.hardwareChartsContainer,
                    connectivityContainer: styles.connectivityContainer,
                    connectivityItem: styles.connectivityItem,
                    statusChipContainer: classNames(
                      styles.statusChipContainer,
                      general.isConnected ? styles.statusChipContainer__online : styles.statusChipContainer__offline,
                    ),
                    isScreenLockedChipContainer: styles.isScreenLockedChipContainer,
                  }}
                  displayEmptyAsDash
                  general={general}
                  hardware={hardware}
                  hideConnectivityStatusChip={isTetraDevice}
                  hideInfo={['connectivityStatus']}
                  hiddenConnectivity={['bluetooth', 'SIM']}
                  image={isTetraDevice ? tetraImage : undefined}
                  isLoadingRefreshButton={isRefetchActionOngoing}
                  onClickRefresh={handleRefreshConfigurations}
                />
                {shouldShowLogicalDevice && (
                  <LogicalDeviceLocationCard
                    dateOfReport={deviceMetrics?.dateOfReport}
                    ipAddress={deviceMetrics?.ipAddress}
                    lastConnection={connectionStatus?.timestamp}
                    location={{
                      lat: deviceMetrics?.lastReportLocation?.lat,
                      lng: deviceMetrics?.lastReportLocation?.lng,
                    }}
                    displayEmptyAsDash
                    geofencing={geofencing}
                    accuracy={deviceMetrics?.accuracy}
                  />
                )}
              </>
            ) : (
              <EmptyList
                image="attach-device"
                messageLabel={t('noDeviceProperties.message', { ns: ALL_LNGS_NAMESPACES.PHYSICAL_DEVICE })}
                namespace={[]}
                titleLabel={t('noDeviceProperties.title', { ns: ALL_LNGS_NAMESPACES.PHYSICAL_DEVICE })}
              />
            )}
            {<LogicalDeviceApplicationsCard {...logicalDeviceProps} />}
            {isToShowDeviceDetails && (
              <>
                {deviceMetrics?.hasMobileNetwork && <LogicalDeviceSIMInformation {...deviceSIMInformation} />}
                <LogicalDeviceBatteryInformation {...deviceBatteryInformation} />
              </>
            )}
          </div>
          <div className={classNames(styles.rightSideElementsContainer)}>
            {isToShowDeviceDetails && isScreenShareAvailable && (
              <div className={styles.desktopActions}>
                <ScreenShareButton
                  isScreenShareAvailable={isScreenShareAvailable}
                  isScreenShareDisabled={isScreenShareDisabled}
                  isScreenShareSessionInProgress={isScreenShareSessionOpened}
                  onClickScreenShare={handleOpenScreenShare}
                />
              </div>
            )}
            {deviceMetrics?.firmware && <LogicalDeviceFirmwareCard firmware={deviceMetrics.firmware} />}
            {onRightColumnExtensionContainerReady && (
              <div className={styles.rightColumnExtensionContainer} ref={onRightColumnExtensionContainerReady} />
            )}
            {isTetraDevice && <TetraCallSettingsCard callScheduling={tetraCallScheduling} />}
            {shouldShowTerminalDetails && (
              <LogicalDeviceTerminalDetailsCard
                metrics={deviceMetrics}
                physicalDevice={logicalDeviceDetails?.attachedPhysicalDevices?.[0]}
                logicalDeviceReference={logicalDeviceDetails?.reference}
              />
            )}
          </div>
        </div>
      </CoreContainer>
    </div>
  );
}
